import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  useGerarAdiantamentos, useDispensarAdiantamento,
  type PendenciaAdiantamentoDoMes, type QuinzenaDaCompetencia,
} from "@/hooks/use-equipe";
import { formatarBRL, formatarDataCurta } from "@shared/equipe-fechamento";
import { RegistrarPagoPorForaDialog } from "./RegistrarPagoPorForaDialog";
import { msgDoErro } from "./msg-do-erro";

/**
 * As TRÊS saídas de um mensalista QUINZENAL sem adiantamento na competência
 * (Financeiro v3, §4.5 — bloco de pendências, linha da tabela e extrato):
 *
 *   • Gerar — só com a quinzena da competência ABERTA e com data de pagamento
 *     (`POST /quinzenas/:id/adiantamentos/gerar`, nasce PREVISTO);
 *   • Registrar pago por fora — `/adiantamentos/externo`, observação obrigatória;
 *   • Não houve adiantamento — `/adiantamentos/dispensa`, motivo obrigatório.
 *
 * Nunca há escape silencioso: sem uma das três o fechamento fica travado
 * (409 ADIANTAMENTO_PENDENTE, no servidor).
 */
export function AdiantamentoAcoes({
  pendencia, competencia, quinzena, compacto,
}: {
  pendencia: PendenciaAdiantamentoDoMes;
  competencia: string;
  /** A primeira quinzena que começa na competência — a que carregaria o adiantamento. */
  quinzena: QuinzenaDaCompetencia | null | undefined;
  compacto?: boolean;
}) {
  const gerar = useGerarAdiantamentos();
  const dispensar = useDispensarAdiantamento();
  const { toast } = useToast();
  const [registrando, setRegistrando] = useState(false);
  const [dispensando, setDispensando] = useState(false);
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    if (dispensando) setMotivo("Gestor confirmou que não houve adiantamento nesta competência.");
  }, [dispensando]);

  const podeGerar = !!quinzena && quinzena.status === "ABERTA" && !!quinzena.dataPagamento;

  const onGerar = async () => {
    if (!quinzena) return;
    try {
      await gerar.mutateAsync({
        quinzenaId: quinzena.id,
        competencia,
        itens: [{ funcionarioId: pendencia.funcionarioId, valor: pendencia.valorSugerido }],
      });
      toast({ title: `Adiantamento de ${formatarBRL(pendencia.valorSugerido)} gerado na quinzena de ${formatarDataCurta(quinzena.dataPagamento)}.` });
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao gerar o adiantamento"), variant: "destructive" });
    }
  };

  const onDispensar = async () => {
    try {
      await dispensar.mutateAsync({ funcionarioId: pendencia.funcionarioId, competencia, motivo: motivo.trim() });
      toast({ title: "Competência conferida — segue com salário cheio." });
      setDispensando(false);
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao marcar a competência"), variant: "destructive" });
    }
  };

  const tam = compacto ? "h-8 text-xs" : "min-h-11 sm:min-h-9 text-sm";

  return (
    <>
      <div className={`flex flex-col sm:flex-row gap-2 ${compacto ? "sm:flex-wrap" : ""}`}>
        <Button
          size="sm"
          className={`flex-1 ${tam}`}
          disabled={!podeGerar || gerar.isPending}
          title={podeGerar ? undefined : quinzena
            ? (quinzena.status !== "ABERTA" ? "A quinzena desta competência já fechou — registre o que foi pago por fora, ou marque que não houve." : "Defina a data de pagamento da quinzena antes de gerar.")
            : "Não há quinzena começando neste mês."}
          onClick={onGerar}
        >
          {gerar.isPending ? "Gerando..." : `Gerar ${formatarBRL(pendencia.valorSugerido)}`}
        </Button>
        <Button size="sm" variant="outline" className={`flex-1 ${tam}`} onClick={() => setRegistrando(true)}>
          Registrar pago por fora
        </Button>
        <Button size="sm" variant="ghost" className={`flex-1 ${tam}`} onClick={() => setDispensando(true)}>
          Não houve adiantamento
        </Button>
      </div>

      <RegistrarPagoPorForaDialog
        open={registrando}
        onOpenChange={setRegistrando}
        pendencia={pendencia}
        competencia={competencia}
      />

      <Dialog open={dispensando} onOpenChange={setDispensando}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Não houve adiantamento</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {pendencia.apelido ?? pendencia.nome} · competência {competencia} — o fechamento vai pagar o
              salário <strong>cheio</strong> ({formatarBRL(pendencia.salarioMensal)}).
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Motivo</Label>
              <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="virou quinzenal no meio do mês" />
              {motivo.trim().length < 3 && (
                <p className="text-[11px] text-erro">Obrigatório — a ausência precisa ser um fato registrado, não uma linha que não existe.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispensando(false)}>Cancelar</Button>
            <Button onClick={onDispensar} disabled={dispensar.isPending || motivo.trim().length < 3}>
              {dispensar.isPending ? "Registrando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
